import React from 'react';
import _ from 'lodash';
import TreeView from '@mui/lab/TreeView';
import TreeItem from '@mui/lab/TreeItem';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Checkbox, FormControlLabel, Typography, Box } from '@mui/material/';

export const FilterTree = React.forwardRef((props, ref) => {
  const { data, initSelected, disableSelection, filterCallback } = props;
  React.useImperativeHandle(ref, () => ({ getTree: () => selected }));
  const [selected, setSelected] = React.useState([]);

  const selectedSet = React.useMemo(() => new Set(selected), [selected]);

  const parentMap = React.useMemo(() => goThroughAllNodes(data), []);

  React.useEffect(() => {
    if (!_.isEmpty(initSelected)) {
      setSelected(JSON.parse(initSelected));
    }
  }, [initSelected]);
  function goThroughAllNodes(nodes, map = {}) {
    if (!nodes.children) {
      return null;
    }

    map[nodes.id] = getAllChild(nodes).splice(1);

    for (const childNode of nodes.children) {
      goThroughAllNodes(childNode, map);
    }

    return map;
  }
  // Get all children from the current node.
  function getAllChild(childNode, collectedNodes = []) {
    if (childNode === null) {
      return collectedNodes;
    }

    collectedNodes.push(childNode.id);

    if (Array.isArray(childNode.children)) {
      for (const node of childNode.children) {
        getAllChild(node, collectedNodes);
      }
    }

    return collectedNodes;
  }

  const getChildById = (nodes, id) => {
    const array = [];
    const path = [];

    // recursive DFS
    function getNodeById(node, id, parentsPath) {
      let result = null;

      if (node.id === id) {
        return node;
      } else if (Array.isArray(node.children)) {
        for (const childNode of node.children) {
          result = getNodeById(childNode, id, parentsPath);

          if (Boolean(result)) {
            parentsPath.push(node.id);

            return result;
          }
        }

        return result;
      }

      return result;
    }

    const nodeToToggle = getNodeById(nodes, id, path);

    return { childNodesToToggle: getAllChild(nodeToToggle, array), path };
  };

  function getOnChange(checked, nodes) {
    const { childNodesToToggle, path } = getChildById(data, nodes.id);
    let array = checked
      ? [...selected, ...childNodesToToggle]
      : selected
        .filter(value => !childNodesToToggle.includes(value))
        .filter(value => !path.includes(value));

    array = array.filter((v, i) => array.indexOf(v) === i);
    const campRegExp = new RegExp('camp-[0-9]+$', 'gm');
    if (nodes.id.match(campRegExp)) {
      const relatedNodes = findRelatedCampChildrens(nodes);
      _.map(relatedNodes, id => {
        const { childNodesToToggle, path } = getChildById(data, id);
        array = checked
          ? [...array, ...childNodesToToggle]
          : array
            .filter(value => !childNodesToToggle.includes(value))
            .filter(value => !path.includes(value));
        array = array.filter((v, i) => array.indexOf(v) === i);
      });
    }
    const allCampRegExp = new RegExp('all_camps-[0-9]+$', 'gm');
    if (nodes.id.match(allCampRegExp)) {
      const relatedNodes = findRelatedCampChildrens(nodes, true);
      _.map(relatedNodes, id => {
        const { childNodesToToggle, path } = getChildById(data, id);
        array = checked
          ? [...array, ...childNodesToToggle]
          : array
            .filter(value => !childNodesToToggle.includes(value))
            .filter(value => !path.includes(value));
        array = array.filter((v, i) => array.indexOf(v) === i);
      });
    }
    setSelected(array);
    if (filterCallback) {
      filterCallback(`tree=${array}`);
    }
  }

  const findRelatedCampChildrens = (node, allCamps = false) => {
    const results = [];
    if (allCamps) {
      _.map(parentMap.all, id => {
        const regexp = new RegExp(`\all_camps-[0-9]+\-${node.id.split('-')[1]}$`, 'gm');
        if (id.match(regexp)) {
          results.push(id);
        }
      });
    } else {
      _.map(parentMap.all, id => {
        const regexp = new RegExp(`\camp-[0-9]+\-${node.id.split('-')[1]}$`, 'gm');
        if (id.match(regexp)) {
          results.push(id);
        }
      });
    }

    return results;
  };

  const TreeItemF = ({ nodes, checked, indeterminate }) => (
    <Box key={nodes.id}>
      <TreeItem
        key={nodes.id}
        nodeId={nodes.id}
        label={ nodes.id == 'all'
          ? <Typography sx={{ padding: 1 }}>{nodes.name}</Typography>
          : <FormControlLabel
            control={
              <Checkbox
                checked={checked}
                indeterminate={!checked && indeterminate}
                onChange={event => getOnChange(event.currentTarget.checked, nodes)
                }
                onClick={e => e.stopPropagation()}
              />
            }
            label={<>{nodes.name}</>}
            key={nodes.id}
          />
        }
      >
        {Array.isArray(nodes.children)
          ? nodes.children.map(node => renderTree(node))
          : null}
      </TreeItem>
    </Box>
  );

  const TreeItemMemo = React.memo(TreeItemF);

  const renderTree = nodes => {
    if (!parentMap) {
      return null;
    }
    const allSelectedChildren = parentMap[
      nodes.id
    ]?.every(childNodeId => selectedSet.has(childNodeId));
    const checked = selectedSet.has(nodes.id) || allSelectedChildren || false;

    const indeterminate =
      parentMap[nodes.id]?.some(childNodeId => selectedSet.has(childNodeId)
      ) || false;

    if (allSelectedChildren && !selectedSet.has(nodes.id)) {
      setSelected([...selected, nodes.id]);
    }

    return <TreeItemMemo nodes={nodes} indeterminate={indeterminate} checked={checked} />;
  };

  return (
    <TreeView
      defaultCollapseIcon={<ExpandMoreIcon />}
      defaultExpanded={['all']}
      defaultExpandIcon={<ChevronRightIcon />}
      defaultEndIcon={null}
    >
      {renderTree(data)}
    </TreeView>
  );
});
